// src/components/tenant/TenantTemplateRouterCore.tsx
import { getTenantId } from "@utils/Auth";
import React, { lazy, Suspense } from "react";

type Loader = () => Promise<{ default: React.ComponentType<any> }>;
type TemplatesMap = Record<string, Loader>;

type Props<P> = {
  templates: TemplatesMap;                 // <- mapa ya creado con import.meta.glob(...)
  DefaultComponent: React.ComponentType<P>;
  passProps: P;
  tenantOverride?: string | null;
  fallback?: React.ReactNode;
  // opcional: cómo resolver el nombre del archivo del template por tenant
  resolveKey?: (tenant: string, keys: string[]) => string | null;
};

export function TenantTemplateRouterCore<P>({
  templates,
  DefaultComponent,
  passProps,
  tenantOverride,
  fallback = <div>Imprimiendo…</div>,
  resolveKey = (tenant, keys) =>
    keys.find((k) => k.endsWith(`/templates/${tenant}/index.tsx`)) ?? null,
}: Props<P>) {
  const [tenant, setTenant] = React.useState<string | null>(tenantOverride ?? null);

  React.useEffect(() => {
    if (tenantOverride != null) return;
    let mounted = true;
    getTenantId().then((t) => mounted && setTenant(t));
    return () => { mounted = false; };
  }, [tenantOverride]);

  if (!tenant) {
    return <Suspense fallback={fallback}><DefaultComponent {...(passProps as any)} /></Suspense>;
  }

  const keys = Object.keys(templates);
  const match = resolveKey(tenant, keys);
  const Loader = match ? templates[match] : null;
  const LazyComp = Loader ? lazy(Loader) : null;

  return (
    <Suspense fallback={fallback}>
      {LazyComp ? <LazyComp {...(passProps as any)} /> : <DefaultComponent {...(passProps as any)} />}
    </Suspense>
  );
}